"use client";

import { useEffect, useRef, useState } from "react";
import { Check, MessageSquare, X } from "lucide-react";
import { submitFeedback } from "@/app/actions/feedback";

const fieldClass =
  "w-full min-h-11 rounded-sm bg-cream px-3 py-2.5 text-base text-text-body focus:outline-none focus:ring-2 focus:ring-forest disabled:opacity-60";

export function FeedbackWidget() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pagePath, setPagePath] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  function openFeedback() {
    if (status === "sent") {
      setStatus("idle");
      setError(null);
    }
    // A dismissed draft keeps the page it describes as well as its text.
    const draft = formRef.current ? new FormData(formRef.current).get("message") : "";
    if (status !== "sending" && (!draft || status === "sent")) {
      setPagePath(window.location.pathname);
    }
    dialogRef.current?.showModal();
    setIsOpen(true);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending") return;
    const formData = new FormData(event.currentTarget);
    setStatus("sending");
    setError(null);
    try {
      const result = await submitFeedback(formData);
      if (result.ok) {
        setStatus("sent");
        formRef.current?.reset();
      } else {
        setStatus("idle");
        setError(result.error);
      }
    } catch {
      setStatus("idle");
      setError("Your feedback could not be sent. Please try again, or email hello@tradesfolk.co.");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={openFeedback}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="feedback-dialog"
        className="fixed right-0 top-1/2 z-[900] flex min-h-11 min-w-11 -translate-y-1/2 items-center gap-2 rounded-l-sm bg-forest px-3 py-4 text-sm font-semibold text-white shadow-md [writing-mode:vertical-rl] hover:bg-forest-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest"
      >
        <MessageSquare size={18} aria-hidden="true" className="shrink-0" />
        Give feedback
      </button>

      <dialog
        ref={dialogRef}
        id="feedback-dialog"
        aria-labelledby="feedback-title"
        aria-describedby="feedback-description"
        onClose={() => setIsOpen(false)}
        className="fixed inset-0 m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-lg bg-warm-bg p-0 text-text-body shadow-xl backdrop:bg-charcoal/45 backdrop:backdrop-blur-sm"
      >
        <div className="relative p-5 sm:p-8">
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            aria-label="Close feedback form"
            className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-sm text-text-muted hover:bg-cream hover:text-text-body focus-visible:outline-2 focus-visible:outline-forest"
          >
            <X size={20} aria-hidden="true" />
          </button>

          <h2 id="feedback-title" className="pr-8 font-serif text-2xl font-semibold text-charcoal">
            {status === "sent" ? "Thanks for your feedback" : "Help us improve Tradesfolk"}
          </h2>
          <p id="feedback-description" className="mt-2 text-sm text-text-muted">
            {status === "sent"
              ? "Your feedback has been sent to hello@tradesfolk.co. We appreciate you taking the time."
              : "Found a bug or have an idea? Tell us what would make Tradesfolk more useful for you."}
          </p>

          {status === "sent" ? (
            <div className="mt-6" role="status">
              <div className="mb-5 flex items-center gap-3 text-success">
                <Check size={24} aria-hidden="true" />
                <span className="font-medium">Feedback sent</span>
              </div>
              <button
                type="button"
                autoFocus
                onClick={() => dialogRef.current?.close()}
                className="min-h-11 w-full rounded-sm bg-forest px-5 py-3 text-sm font-semibold text-white hover:bg-forest-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest"
              >
                Done
              </button>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className="mt-6" aria-busy={status === "sending"}>
              <input type="hidden" name="page" value={pagePath} />
              <div className="hidden" aria-hidden="true">
                <label htmlFor="feedback-website">Leave this field empty</label>
                <input id="feedback-website" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <fieldset disabled={status === "sending"} className="space-y-4">
                <div>
                  <label htmlFor="feedback-type" className="mb-1.5 block text-sm font-medium">
                    What would you like to share?
                  </label>
                  <select id="feedback-type" name="type" defaultValue="bug" className={fieldClass}>
                    <option value="bug">Report a bug</option>
                    <option value="feature">Suggest a feature</option>
                    <option value="improvement">Suggest an improvement</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="feedback-message" className="mb-1.5 block text-sm font-medium">
                    Your feedback
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    rows={4}
                    required
                    minLength={10}
                    maxLength={3000}
                    placeholder="What happened, or what would you like to see?"
                    aria-describedby="feedback-message-hint"
                    className={`${fieldClass} resize-y`}
                  />
                  <p id="feedback-message-hint" className="mt-1 text-xs text-text-muted">
                    For bugs, tell us what you were trying to do and what went wrong.
                  </p>
                </div>
                <div>
                  <label htmlFor="feedback-email" className="mb-1.5 block text-sm font-medium">
                    Your email <span className="font-normal text-text-muted">(optional)</span>
                  </label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    maxLength={254}
                    aria-describedby="feedback-email-hint"
                    className={fieldClass}
                  />
                  <p id="feedback-email-hint" className="mt-1 text-xs text-text-muted">
                    Add this if you would like us to reply.
                  </p>
                </div>
                <p className="break-all text-xs text-text-muted">
                  Page included with your feedback: {pagePath}
                </p>
                {error && <p role="alert" className="text-sm text-error">{error}</p>}
                <button
                  type="submit"
                  className="min-h-11 w-full rounded-sm bg-forest px-5 py-3 text-sm font-semibold text-white hover:bg-forest-light focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest disabled:cursor-wait disabled:opacity-60"
                >
                  {status === "sending" ? "Sending feedback…" : "Send feedback"}
                </button>
              </fieldset>
              <p className="mt-4 text-center text-xs text-text-muted">
                Sent to{" "}
                <a href="mailto:hello@tradesfolk.co" className="underline underline-offset-2 hover:text-text-body">
                  hello@tradesfolk.co
                </a>
              </p>
            </form>
          )}
        </div>
      </dialog>
    </>
  );
}
